/**
 * The change planner's state (docs/plans/change-planner.md §6): the tenant's plans, the one open in
 * the tab with its local draft (edits until Save), what is busy, and the stream's messages applied.
 * Types come from the generated client; nothing here classifies a plan, the backend did.
 */
import { create } from 'zustand';
import { client, errorText } from '@/lib/api';
import type { plans } from '@/lib/client';
import { openPlanStream } from '@/lib/planStream';
import { useBrowserStore } from './browserStore';

export interface Draft {
  planId: string;
  version: number;
  title: string;
  steps: plans.PlanStepInput[];
  dirty: boolean;
  /** Another version arrived while the draft was dirty. */
  stale: boolean;
}

/** A channel as the browser panel's line names it. */
const CHANNEL_WORD: Record<plans.Channel, string> = { api: 'API', browser: 'Browser', human: 'You' };

export type Busy = 'create' | 'load' | 'save' | 'approve' | 'run' | 'cancel' | 'revert' | 'mark-done' | 'schedule';

interface PlansState {
  list: plans.PlanSummary[];
  listTotal: number;
  listLoading: boolean;
  listFilter: plans.PlanStatus | 'all';
  plansById: Record<string, plans.PlanView>;
  selectedStepKey: string | null;
  draft: Draft | null;
  busy: Partial<Record<Busy, boolean>>;
  error: string | null;

  loadList(filter?: plans.PlanStatus | 'all'): Promise<void>;
  /** The next page of the list, appended. */
  loadMore(): Promise<void>;
  loadPlan(id: string): Promise<plans.PlanView | null>;
  /** A prompt read as a request document (docs/plans/chat-on-document-pipeline.md): its id, null when it fails. */
  createFromPrompt(prompt: string): Promise<string | null>;
  selectStep(key: string | null): void;
  editStep(key: string, patch: Partial<plans.PlanStepInput>): void;
  editInput(key: string, name: string, value: string): void;
  removeStep(key: string): void;
  editTitle(title: string): void;
  saveDraft(): Promise<void>;
  discardDraft(): void;
  approve(id: string, options?: { run?: boolean; watchBrowser?: boolean }): Promise<void>;
  run(id: string): Promise<void>;
  cancel(id: string): Promise<void>;
  revert(id: string): Promise<string | null>;
  /** A partly reverted plan's failed undos tried again. */
  revertAgain(id: string): Promise<void>;
  markHumanDone(id: string, key: string): Promise<void>;
  schedule(id: string, atIso: string): Promise<boolean>;
  unschedule(id: string): Promise<void>;
  /** D11: the chat card's Approve & run — approve, run, and follow the plan until it ends, its line into the chat. */
  approveAndRunFromChat(id: string, watchBrowser: boolean, say: (line: string) => void): Promise<void>;
  applyStreamMessage(message: plans.PlanStreamMessage): void;
  applyPlan(plan: plans.PlanView): void;
  clearError(): void;
}

const message = errorText;

export const LIST_PAGE = 50;

const listQuery = (filter: plans.PlanStatus | 'all', offset: number): plans.ListPlansRequest =>
  filter === 'all' ? { limit: LIST_PAGE, offset } : { status: filter, limit: LIST_PAGE, offset };

const byUpdated = (a: plans.PlanSummary, b: plans.PlanSummary): number => b.updated_at.localeCompare(a.updated_at);

/** A view's steps as the editable input shape (the position is the list's order). */
function stepsOf(plan: plans.PlanView): plans.PlanStepInput[] {
  return plan.steps.map(({ channels_available: _c, missing_inputs: _m, missing_scopes: _s, run: _r, position: _p, ...step }) => step);
}

function freshDraft(plan: plans.PlanView): Draft {
  return { planId: plan.id, version: plan.version, title: plan.title, steps: stepsOf(plan), dirty: false, stale: false };
}

export const usePlansStore = create<PlansState>((set, get) => ({
  list: [],
  listTotal: 0,
  listLoading: false,
  listFilter: 'all',
  plansById: {},
  selectedStepKey: null,
  draft: null,
  busy: {},
  error: null,

  async loadList(filter) {
    const listFilter = filter ?? get().listFilter;
    set({ listLoading: true, listFilter });
    try {
      const reply = await client.plans.listPlans(listQuery(listFilter, 0));
      set({ list: reply.plans, listTotal: reply.total, listLoading: false });
    } catch (err) {
      set({ listLoading: false, error: message(err) });
    }
  },

  async loadMore() {
    const { list, listFilter, listLoading } = get();
    if (listLoading) return;
    set({ listLoading: true });
    try {
      const reply = await client.plans.listPlans(listQuery(listFilter, list.length));
      const seen = new Set(list.map((item) => item.id));
      set({ list: [...list, ...reply.plans.filter((item) => !seen.has(item.id))], listTotal: reply.total, listLoading: false });
    } catch (err) {
      set({ listLoading: false, error: message(err) });
    }
  },

  async loadPlan(id) {
    set((s) => ({ busy: { ...s.busy, load: true } }));
    try {
      const { plan } = await client.plans.getPlan(id);
      get().applyPlan(plan);
      const draft = get().draft;
      if (draft === null || draft.planId !== plan.id || !draft.dirty) set({ draft: freshDraft(plan) });
      return plan;
    } catch (err) {
      set({ error: message(err) });
      return null;
    } finally {
      set((s) => ({ busy: { ...s.busy, load: false } }));
    }
  },

  async createFromPrompt(prompt) {
    set((s) => ({ busy: { ...s.busy, create: true }, error: null }));
    try {
      const reply = await client.plans.createPlan({ source: { kind: 'prompt', prompt } });
      return reply.document?.id ?? null;
    } catch (err) {
      set({ error: message(err) });
      return null;
    } finally {
      set((s) => ({ busy: { ...s.busy, create: false } }));
    }
  },


  selectStep(key) {
    set({ selectedStepKey: key });
  },

  editStep(key, patch) {
    set((s) => (s.draft === null ? {} : { draft: { ...s.draft, dirty: true, steps: s.draft.steps.map((step) => (step.key === key ? { ...step, ...patch } : step)) } }));
  },

  editInput(key, name, value) {
    set((s) => {
      if (s.draft === null) return {};
      const steps = s.draft.steps.map((step) =>
        step.key === key ? { ...step, inputs: step.inputs.map((input) => (input.name === name ? { ...input, value: value === '' ? null : value } : input)) } : step,
      );
      return { draft: { ...s.draft, dirty: true, steps } };
    });
  },

  removeStep(key) {
    set((s) => {
      if (s.draft === null) return {};
      const steps = s.draft.steps.filter((step) => step.key !== key).map((step) => ({ ...step, depends_on: step.depends_on.filter((dep) => dep !== key) }));
      return { draft: { ...s.draft, dirty: true, steps }, selectedStepKey: s.selectedStepKey === key ? null : s.selectedStepKey };
    });
  },

  editTitle(title) {
    set((s) => (s.draft === null ? {} : { draft: { ...s.draft, title, dirty: true } }));
  },

  async saveDraft() {
    const draft = get().draft;
    if (draft === null || !draft.dirty) return;
    set((s) => ({ busy: { ...s.busy, save: true }, error: null }));
    try {
      const { plan } = await client.plans.updatePlan(draft.planId, { expected_version: draft.version, title: draft.title, steps: draft.steps });
      get().applyPlan(plan);
      set({ draft: freshDraft(plan) });
    } catch (err) {
      set({ error: message(err) });
    } finally {
      set((s) => ({ busy: { ...s.busy, save: false } }));
    }
  },

  discardDraft() {
    const draft = get().draft;
    const plan = draft === null ? undefined : get().plansById[draft.planId];
    set({ draft: plan ? freshDraft(plan) : null });
  },

  async approve(id, options) {
    const plan = get().plansById[id];
    if (!plan) return;
    set((s) => ({ busy: { ...s.busy, approve: true }, error: null }));
    try {
      const reply = await client.plans.approvePlan(id, { version: plan.version, run: options?.run, watch_browser: options?.watchBrowser });
      get().applyPlan(reply.plan);
    } catch (err) {
      set({ error: message(err) });
    } finally {
      set((s) => ({ busy: { ...s.busy, approve: false } }));
    }
  },

  async run(id) {
    set((s) => ({ busy: { ...s.busy, run: true }, error: null }));
    try {
      await client.plans.runPlan(id);
      await get().loadPlan(id);
    } catch (err) {
      set({ error: message(err) });
    } finally {
      set((s) => ({ busy: { ...s.busy, run: false } }));
    }
  },


  async cancel(id) {
    set((s) => ({ busy: { ...s.busy, cancel: true }, error: null }));
    try {
      const { plan } = await client.plans.cancelPlan(id);
      get().applyPlan(plan);
    } catch (err) {
      set({ error: message(err) });
    } finally {
      set((s) => ({ busy: { ...s.busy, cancel: false } }));
    }
  },

  async revertAgain(id) {
    set((s) => ({ busy: { ...s.busy, revert: true }, error: null }));
    try {
      const { plan } = await client.plans.revertAgain(id);
      get().applyPlan(plan);
    } catch (err) {
      set({ error: message(err) });
    } finally {
      set((s) => ({ busy: { ...s.busy, revert: false } }));
    }
  },

  async revert(id) {
    set((s) => ({ busy: { ...s.busy, revert: true }, error: null }));
    try {
      const { plan } = await client.plans.revertPlan(id);
      get().applyPlan(plan);
      return plan.id;
    } catch (err) {
      set({ error: message(err) });
      return null;
    } finally {
      set((s) => ({ busy: { ...s.busy, revert: false } }));
    }
  },

  async markHumanDone(id, key) {
    set((s) => ({ busy: { ...s.busy, 'mark-done': true }, error: null }));
    try {
      const { plan } = await client.plans.markHumanStepDone(id, key, {});
      get().applyPlan(plan);
    } catch (err) {
      set({ error: message(err) });
    } finally {
      set((s) => ({ busy: { ...s.busy, 'mark-done': false } }));
    }
  },

  async schedule(id, atIso) {
    set((s) => ({ busy: { ...s.busy, schedule: true }, error: null }));
    try {
      const { plan } = await client.plans.schedulePlan(id, { at: atIso });
      get().applyPlan(plan);
      return true;
    } catch (err) {
      set({ error: message(err) });
      return false;
    } finally {
      set((s) => ({ busy: { ...s.busy, schedule: false } }));
    }
  },

  async unschedule(id) {
    set((s) => ({ busy: { ...s.busy, schedule: true }, error: null }));
    try {
      const { plan } = await client.plans.unschedulePlan(id);
      get().applyPlan(plan);
    } catch (err) {
      set({ error: message(err) });
    } finally {
      set((s) => ({ busy: { ...s.busy, schedule: false } }));
    }
  },

  async approveAndRunFromChat(id, watchBrowser, say) {
    set((s) => ({ busy: { ...s.busy, approve: true }, error: null }));
    try {
      const { plan } = await client.plans.getPlan(id);
      get().applyPlan(plan);
      // Approve and run in one call; a plan someone approved already just runs; one already running is followed.
      if (plan.status === 'approved' || plan.status === 'scheduled') await client.plans.runPlan(id);
      else if (plan.status !== 'running' && plan.status !== 'reverting') {
        const reply = await client.plans.approvePlan(id, { version: plan.version, run: true, watch_browser: watchBrowser });
        get().applyPlan(reply.plan);
      }
      say(`Running "${plan.title}"…`);
    } catch (err) {
      const text = message(err);
      set({ error: text });
      say(`Couldn't run the plan: ${text}`);
      return;
    } finally {
      set((s) => ({ busy: { ...s.busy, approve: false } }));
    }
    // Follow it until it ends; the backend stores the same line in the conversation (D11), so this one is live only.
    await new Promise<void>((resolve) => {
      const close = openPlanStream({ planId: id }, (msg) => {
        get().applyStreamMessage(msg);
        const plan = msg.type === 'plan' ? msg.plan : undefined;
        if (plan?.outcome_line) {
          say(plan.outcome_line);
          close();
          resolve();
        }
      });
    });
  },

  applyStreamMessage(msg) {
    if (msg.type === 'plan' && msg.plan) get().applyPlan(msg.plan);
    else if (msg.type === 'summary' && msg.summary) {
      const summary = msg.summary;
      set((s) => {
        const index = s.list.findIndex((item) => item.id === summary.id);
        // A plan that left the filtered status leaves the list; one that entered it (or changed) takes its place by recency.
        if (s.listFilter !== 'all' && summary.status !== s.listFilter) {
          return index === -1 ? {} : { list: s.list.filter((item) => item.id !== summary.id), listTotal: Math.max(0, s.listTotal - 1) };
        }
        const list = index === -1 ? [summary, ...s.list] : s.list.map((item) => (item.id === summary.id ? summary : item));
        return { list: list.sort(byUpdated), listTotal: index === -1 ? s.listTotal + 1 : s.listTotal };
      });
    }
  },

  applyPlan(plan) {
    set((s) => {
      const current = s.plansById[plan.id];
      if (current && (current.version > plan.version || (current.version === plan.version && current.updated_at > plan.updated_at))) return {};
      let draft = s.draft;
      if (draft !== null && draft.planId === plan.id) {
        if (!draft.dirty) draft = freshDraft(plan);
        else if (plan.version > draft.version) draft = { ...draft, stale: true };
      }
      return { plansById: { ...s.plansById, [plan.id]: plan }, draft };
    });
    // The step ZAC is on, whatever its channel (live 2026-11-03: the banner stayed on step 1 through five API steps,
    // the panel blocked): while the plan is undone, the step being undone, not one its stopped run left running. Only
    // a step on the browser (by where it runs: an API step handed over runs there) takes the panel; any other is a
    // line over it that leaves it free.
    const ordered = [...plan.steps].sort((a, b) => a.position - b.position);
    const reverting = plan.status === 'reverting';
    const active = plan.status === 'running' || reverting;
    const at = active ? ordered.findIndex((step) => (reverting ? step.run?.revert_status === 'pending' : step.run?.status === 'running')) : -1;
    const current = at === -1 ? undefined : ordered[at];
    const onBrowser = current !== undefined && (current.run?.channel ?? current.channel) === 'browser';
    const label = current === undefined ? null : `${reverting ? 'Undoing step' : 'Step'} ${at + 1}/${ordered.length} · ${CHANNEL_WORD[current.run?.channel ?? current.channel]}: ${current.title}`;
    const browser = useBrowserStore.getState();
    if (onBrowser && plan.watch_browser) {
      browser.openPanel();
      browser.setAgentControl(true);
      browser.setDrivingStatus(label);
      browser.setRunNote(null);
    } else if (active) {
      if (browser.agentControl) browser.setAgentControl(false);
      browser.setDrivingStatus(null);
      browser.setRunNote(label);
    } else if (browser.agentControl || browser.runNote !== null) {
      browser.setAgentControl(false);
      browser.setDrivingStatus(null);
      browser.setRunNote(null);
    }
  },

  clearError() {
    set({ error: null });
  },
}));
